import React, { useState } from "react";
import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  SimpleGrid,
  Icon,
  useColorModeValue,
  VStack,
  HStack,
  Badge,
  Grid,
  GridItem,
  chakra,
  Divider,
  Tag,
  TagLabel,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Image,
  Flex,
} from "@chakra-ui/react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaLightbulb,
  FaRocket,
  FaHistory,
  FaQuoteLeft,
  FaChessKnight,
  FaHandsHelping,
  FaBalanceScale,
  FaEye,
  FaBriefcase,
  FaStar,
  FaComments,
  FaCalendarAlt,
  FaChartLine,
  FaLaptopCode,
  FaCertificate,
  FaSeedling,
  FaSchool,
  FaHandshake,
  FaBuilding,
  FaYoutube,
  FaPlus,
  FaMinus,
} from "react-icons/fa";
import { RiThunderstormsFill } from "react-icons/ri";
import TeamSlider from "./TeamSlider";
import CompaniesSlider from "./Companiesslider";
import StatsSection from "./Statssection"; // ← separated component
import StoryImg from "../../assets/images/teeam.png";

const MotionBox = motion(Box);

/* ══════════════════════════════════════════════════
   GLOBAL BEAN KEYFRAMES
══════════════════════════════════════════════════ */
const GlobalStyles = () => (
  <style>{`
    @keyframes bean1  {0%,100%{transform:translate(0,0)    rotate(30deg)  scaleX(2);}50%{transform:translate(12px,-30px)  rotate(48deg)  scaleX(2);}}
    @keyframes bean2  {0%,100%{transform:translate(0,0)    rotate(-20deg) scaleX(2);}50%{transform:translate(-15px,-25px) rotate(-5deg)  scaleX(2);}}
    @keyframes bean3  {0%,100%{transform:translate(0,0)    rotate(55deg)  scaleX(2);}50%{transform:translate(10px,-35px)  rotate(72deg)  scaleX(2);}}
    @keyframes bean4  {0%,100%{transform:translate(0,0)    rotate(-40deg) scaleX(2);}50%{transform:translate(-8px,-28px)  rotate(-22deg) scaleX(2);}}
    @keyframes bean5  {0%,100%{transform:translate(0,0)    rotate(10deg)  scaleX(2);}50%{transform:translate(14px,-32px)  rotate(28deg)  scaleX(2);}}
    @keyframes bean6  {0%,100%{transform:translate(0,0)    rotate(70deg)  scaleX(2);}50%{transform:translate(-10px,-20px) rotate(88deg)  scaleX(2);}}
    @keyframes bean7  {0%,100%{transform:translate(0,0)    rotate(-60deg) scaleX(2);}50%{transform:translate(16px,-38px)  rotate(-42deg) scaleX(2);}}
    @keyframes bean8  {0%,100%{transform:translate(0,0)    rotate(25deg)  scaleX(2);}50%{transform:translate(-12px,-22px) rotate(43deg)  scaleX(2);}}
    @keyframes bean9  {0%,100%{transform:translate(0,0)    rotate(-15deg) scaleX(2);}50%{transform:translate(9px,-40px)   rotate(3deg)   scaleX(2);}}
    @keyframes bean10 {0%,100%{transform:translate(0,0)    rotate(80deg)  scaleX(2);}50%{transform:translate(-13px,-26px) rotate(98deg)  scaleX(2);}}
    @keyframes bean11 {0%,100%{transform:translate(0,0)    rotate(-50deg) scaleX(2);}50%{transform:translate(11px,-34px)  rotate(-32deg) scaleX(2);}}
    @keyframes bean12 {0%,100%{transform:translate(0,0)    rotate(45deg)  scaleX(2);}50%{transform:translate(-7px,-18px)  rotate(63deg)  scaleX(2);}}
    .gt-bean { position:absolute; border-radius:50%; pointer-events:none; }
  `}</style>
);

/* ══════════════════════════════════════════════════
   FLOATING BEANS
══════════════════════════════════════════════════ */
const beanDefs = [
  { w: 180, h: 90, top: "4%", left: "2%", anim: "bean1", dur: 16, op: 0.12 },
  { w: 110, h: 55, top: "18%", left: "90%", anim: "bean2", dur: 20, op: 0.09 },
  { w: 200, h: 100, top: "40%", left: "78%", anim: "bean3", dur: 24, op: 0.1 },
  { w: 90, h: 45, top: "62%", left: "4%", anim: "bean4", dur: 18, op: 0.11 },
  { w: 150, h: 75, top: "78%", left: "58%", anim: "bean5", dur: 22, op: 0.08 },
  { w: 80, h: 40, top: "30%", left: "45%", anim: "bean6", dur: 17, op: 0.07 },
  { w: 190, h: 95, top: "52%", left: "18%", anim: "bean7", dur: 26, op: 0.09 },
  { w: 120, h: 60, top: "8%", left: "52%", anim: "bean8", dur: 19, op: 0.1 },
  { w: 140, h: 70, top: "88%", left: "30%", anim: "bean9", dur: 23, op: 0.08 },
  { w: 100, h: 50, top: "70%", left: "85%", anim: "bean10", dur: 21, op: 0.09 },
  { w: 160, h: 80, top: "25%", left: "68%", anim: "bean11", dur: 25, op: 0.07 },
  { w: 75, h: 38, top: "55%", left: "55%", anim: "bean12", dur: 15, op: 0.08 },
];

const BeansBackground = ({ color }) => (
  <Box
    position="absolute"
    inset={0}
    overflow="hidden"
    zIndex={0}
    pointerEvents="none"
  >
    {beanDefs.map((b, i) => (
      <div
        key={i}
        className="gt-bean"
        style={{
          width: b.w,
          height: b.h,
          top: b.top,
          left: b.left,
          border: `2px solid ${color}`,
          opacity: b.op,
          animation: `${b.anim} ${b.dur}s ease-in-out infinite`,
        }}
      />
    ))}
  </Box>
);

/* ══════════════════════════════════════════════════
   TIMELINE DATA
══════════════════════════════════════════════════ */
const timelineEvents = [
  {
    period: "Dec 2022",
    icon: FaSeedling,
    num: "01",
    badge: "Founded",
    badgeColor: "blue",
    title: "Two Minds, One Mission",
    desc: "GrapplTech was born with just 2 founding members in a small room, driven by one observation: brilliant graduates were failing interviews because they lacked real industry context.",
  },
  {
    period: "2023",
    icon: FaSchool,
    num: "02",
    badge: "Community",
    badgeColor: "purple",
    title: "Taking It to the Campuses",
    desc: "We hit the road — conducting offline demo sessions across colleges, meeting students where they were. Hundreds of students trusted us early, and that trust became our foundation.",
  },
  {
    period: "2024",
    icon: FaHandshake,
    num: "03",
    badge: "Milestone",
    badgeColor: "green",
    title: "Partner Programs — A Massive Hit",
    desc: "We launched a structured affiliate ecosystem — empowering college faculty, campus ambassadors, and individual advocates to refer learners and earn weekly, performance-based commissions. This transparent, trust-first model accelerated our growth nationwide.",
  },
  {
    period: "2025",
    icon: FaBuilding,
    num: "04",
    badge: "4.8 ★ AmbitionBox",
    badgeColor: "yellow",
    title: "Grapplers Power Startups",
    desc: "We began placing our top students — Grapplers — with early-stage startups building MVPs on lean budgets. This became the most reliable path to a first internship. We're now rated 4.8 ⭐ on AmbitionBox with 250+ verified reviews.",
  },
  {
    period: "2026",
    icon: FaYoutube,
    num: "05",
    badge: "Coming Soon",
    badgeColor: "red",
    title: "Going Open — Free Courses & SaaS",
    desc: "Our boldest move: a full SaaS product for AI-driven career roadmapping and a free YouTube course series — democratizing elite tech education for every ambitious engineer, everywhere.",
  },
];

/* ══════════════════════════════════════════════════
   GLASSMORPHIC TIMELINE CARD
══════════════════════════════════════════════════ */
const TimelineCard = ({ evt, accentColor, textMuted, isLight }) => {
  const glassBg = isLight ? "rgba(255,255,255,0.82)" : "rgba(26,32,44,0.82)";
  const glassBdr = isLight ? "rgba(2,142,196,0.18)" : "rgba(2,142,196,0.24)";
  const shadowSm = isLight
    ? "0 4px 32px rgba(2,142,196,0.07), 0 1px 6px rgba(0,0,0,0.06)"
    : "0 4px 32px rgba(2,142,196,0.14), 0 1px 6px rgba(0,0,0,0.28)";
  const shadowLg = isLight
    ? "0 14px 50px rgba(2,142,196,0.16), 0 2px 12px rgba(0,0,0,0.08)"
    : "0 14px 50px rgba(2,142,196,0.30), 0 2px 12px rgba(0,0,0,0.42)";

  return (
    <MotionBox
      whileHover={{ y: -7, scale: 1.012 }}
      transition={{ duration: 0.2 }}
      position="relative"
      p={{ base: 6, md: 8 }}
      rounded="3xl"
      overflow="hidden"
      style={{
        background: glassBg,
        border: `1.5px solid ${glassBdr}`,
        backdropFilter: "blur(18px)",
        WebkitBackdropFilter: "blur(18px)",
        boxShadow: shadowSm,
      }}
      _hover={{ style: { boxShadow: shadowLg } }}
    >
      <Text
        position="absolute"
        top="-8px"
        right="18px"
        fontSize="108px"
        fontWeight="900"
        lineHeight={1}
        fontFamily="Bricolage Grotesque, sans-serif"
        color={accentColor}
        opacity={0.055}
        userSelect="none"
        pointerEvents="none"
      >
        {evt.num}
      </Text>
      <Flex
        justify="space-between"
        align="center"
        mb={5}
        flexWrap="wrap"
        gap={2}
      >
        <HStack spacing={2}>
          <Box
            w="32px"
            h="32px"
            rounded="lg"
            bg={accentColor}
            display="flex"
            alignItems="center"
            justifyContent="center"
            shadow="md"
          >
            <Icon as={evt.icon} color="white" boxSize={3.5} />
          </Box>
          <Text
            fontWeight="800"
            fontSize="xs"
            color={accentColor}
            letterSpacing="0.14em"
            textTransform="uppercase"
          >
            {evt.period}
          </Text>
        </HStack>
        <Badge
          colorScheme={evt.badgeColor}
          rounded="full"
          px={3}
          py={1}
          fontSize="10px"
          fontWeight="700"
          letterSpacing="0.05em"
        >
          {evt.badge}
        </Badge>
      </Flex>
      <Heading
        size={{ base: "sm", md: "md" }}
        fontFamily="Bricolage Grotesque"
        mb={3}
        lineHeight="1.3"
      >
        {evt.title}
      </Heading>
      <Text
        color={textMuted}
        fontSize={{ base: "sm", md: "md" }}
        lineHeight="1.75"
      >
        {evt.desc}
      </Text>
      <Box
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        h="3px"
        opacity={0.5}
        rounded="full"
        style={{
          background: `linear-gradient(to right, transparent, ${accentColor}, transparent)`,
        }}
      />
    </MotionBox>
  );
};

/* ══════════════════════════════════════════════════
   ZIG-ZAG TIMELINE
══════════════════════════════════════════════════ */
const ZigZagTimeline = ({ accentColor, textMuted, isLight }) => (
  <Box position="relative">
    <Box
      display={{ base: "none", lg: "block" }}
      position="absolute"
      left="50%"
      top="60px"
      bottom="60px"
      w="2px"
      transform="translateX(-50%)"
      zIndex={1}
      style={{
        background: `linear-gradient(to bottom, transparent, ${accentColor}70, ${accentColor}70, transparent)`,
      }}
    />
    <VStack spacing={{ base: 5, lg: 3 }} align="stretch">
      {timelineEvents.map((evt, i) => {
        const isLeft = i % 2 === 0;
        return (
          <Box key={i} position="relative">
            <Box
              display={{ base: "none", lg: "flex" }}
              position="absolute"
              left="50%"
              top="50%"
              transform="translate(-50%,-50%)"
              zIndex={3}
              w="48px"
              h="48px"
              rounded="full"
              bg={accentColor}
              alignItems="center"
              justifyContent="center"
              shadow={`0 0 0 7px ${accentColor}22`}
              border={`3px solid ${isLight ? "white" : "#05060b"}`}
            >
              <Icon as={evt.icon} color="white" boxSize={4} />
            </Box>
            <Grid
              display={{ base: "none", lg: "grid" }}
              templateColumns="1fr 80px 1fr"
              alignItems="center"
            >
              <GridItem pr={10} py={5}>
                {isLeft ? (
                  <TimelineCard
                    evt={evt}
                    accentColor={accentColor}
                    textMuted={textMuted}
                    isLight={isLight}
                  />
                ) : (
                  <Box />
                )}
              </GridItem>
              <GridItem />
              <GridItem pl={10} py={5}>
                {!isLeft ? (
                  <TimelineCard
                    evt={evt}
                    accentColor={accentColor}
                    textMuted={textMuted}
                    isLight={isLight}
                  />
                ) : (
                  <Box />
                )}
              </GridItem>
            </Grid>
            <Box display={{ base: "block", lg: "none" }}>
              <HStack mb={3} spacing={2}>
                <Box
                  w="28px"
                  h="28px"
                  rounded="full"
                  bg={accentColor}
                  flexShrink={0}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  shadow={`0 0 0 4px ${accentColor}22`}
                >
                  <Icon as={evt.icon} color="white" boxSize={3} />
                </Box>
                <Box
                  h="2px"
                  flex={1}
                  style={{
                    background: `linear-gradient(to right, ${accentColor}60, transparent)`,
                  }}
                />
              </HStack>
              <TimelineCard
                evt={evt}
                accentColor={accentColor}
                textMuted={textMuted}
                isLight={isLight}
              />
            </Box>
          </Box>
        );
      })}
    </VStack>
  </Box>
);

/* ══════════════════════════════════════════════════
   ACCORDION FAQ ITEM
══════════════════════════════════════════════════ */
const FaqItem = ({ q, a, index, accentColor, isOpen, onToggle }) => {
  const cardBg = useColorModeValue("white", "gray.800");
  const borderCol = useColorModeValue("gray.100", "whiteAlpha.100");
  const textMuted = useColorModeValue("gray.600", "gray.400");
  const headCol = useColorModeValue("gray.900", "white");

  return (
    <Box
      bg={cardBg}
      border="1.5px solid"
      borderColor={isOpen ? accentColor : borderCol}
      rounded="2xl"
      overflow="hidden"
      transition="border-color 0.25s ease, box-shadow 0.25s ease"
      style={{
        boxShadow: isOpen
          ? `0 0 0 3px ${accentColor}18, 0 8px 32px rgba(2,142,196,0.08)`
          : "none",
      }}
    >
      {/* trigger */}
      <Box
        as="button"
        w="full"
        textAlign="left"
        px={{ base: 5, md: 8 }}
        py={{ base: 5, md: 6 }}
        onClick={onToggle}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={4}
        _hover={{ bg: useColorModeValue("gray.50", "whiteAlpha.50") }}
        transition="background 0.2s ease"
      >
        <HStack spacing={4} flex={1} align="center">
          {/* number badge */}
          <Box
            flexShrink={0}
            w="28px"
            h="28px"
            rounded="md"
            display="flex"
            alignItems="center"
            justifyContent="center"
            fontSize="11px"
            fontWeight="900"
            fontFamily="Bricolage Grotesque, sans-serif"
            style={{
              background: isOpen ? accentColor : `${accentColor}18`,
              color: isOpen ? "white" : accentColor,
              transition: "background 0.25s ease, color 0.25s ease",
            }}
          >
            {String(index + 1).padStart(2, "0")}
          </Box>
          <Text
            fontWeight="700"
            fontSize={{ base: "md", md: "lg" }}
            color={headCol}
            lineHeight="1.4"
          >
            {q}
          </Text>
        </HStack>

        {/* +/− icon */}
        <Box
          flexShrink={0}
          w="32px"
          h="32px"
          rounded="lg"
          display="flex"
          alignItems="center"
          justifyContent="center"
          style={{
            background: isOpen ? accentColor : `${accentColor}12`,
            transition: "background 0.25s ease",
          }}
        >
          <Icon
            as={isOpen ? FaMinus : FaPlus}
            color={isOpen ? "white" : accentColor}
            boxSize={3}
          />
        </Box>
      </Box>

      {/* collapsible answer */}
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="answer"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
            style={{ overflow: "hidden" }}
          >
            <Box px={{ base: 5, md: 8 }} pb={{ base: 5, md: 7 }} pt={0}>
              {/* accent divider */}
              <Box
                h="1px"
                mb={4}
                style={{
                  background: `linear-gradient(to right, ${accentColor}40, transparent)`,
                }}
              />
              <Text
                color={textMuted}
                lineHeight="1.85"
                fontSize={{ base: "sm", md: "md" }}
                pl={{ md: "44px" }}
              >
                {a}
              </Text>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

/* ══════════════════════════════════════════════════
   MAIN ABOUT COMPONENT
══════════════════════════════════════════════════ */
const About = () => {
  const textMuted = useColorModeValue("gray.600", "gray.400");
  const cardBg = useColorModeValue("white", "gray.800");
  const mainBg = useColorModeValue("#FDFDFD", "#05060b");
  const borderColor = useColorModeValue("gray.100", "whiteAlpha.100");
  const isLight = useColorModeValue(true, false);
  const accentColor = "#028EC4";

  /* accordion open state */
  const [openFaq, setOpenFaq] = useState(0);

  /* ── data ── */
  const coreValues = [
    {
      icon: FaChessKnight,
      title: "Ownership Mentality",
      desc: "We don't just complete tasks; we own outcomes. Engineers here think like founders, looking beyond the ticket to the long-term impact.",
    },
    {
      icon: RiThunderstormsFill,
      title: "Bias for Action",
      desc: "Speed matters in tech. We value calculated risk-taking and momentum over over-analysis. Done is better than perfect.",
    },
    {
      icon: FaHandsHelping,
      title: "Open Company, No Bullshit",
      desc: "We foster radical transparency. Information is shared by default, and feedback is direct, frequent, and kind.",
    },
    {
      icon: FaBalanceScale,
      title: "Customer Obsession",
      desc: "We start with the learner and work backwards. Every feature and lesson is vetted against the value it adds to their career.",
    },
    {
      icon: FaEye,
      title: "Hire and Develop the Best",
      desc: "We raise the performance bar with every hire and every cohort. We recognize exceptional talent and provide friction to help them grow.",
    },
    {
      icon: FaLightbulb,
      title: "Invent and Simplify",
      desc: "We expect innovation from every team member. We look for ways to simplify complex architectures and legacy processes.",
    },
  ];

  const testimonials = [
    {
      name: "Nithin Krishna",
      role: "Software Engineer @ TCS",
      cohort: "Cohort 3",
      rating: 5,
      quote:
        "I was placed at TCS after struggling for almost a year. GrapplTech's mock interviews and structured DSA prep gave me the clarity and confidence I was missing. Best decision I ever made.",
    },
    {
      name: "Sneha Prasad",
      role: "Associate Consultant @ Infosys",
      cohort: "Cohort 5",
      rating: 5,
      quote:
        "The offline college sessions first introduced me to GrapplTech, and I was immediately hooked. The mentors genuinely care — they stayed up late with me the night before my Infosys interview.",
    },
    {
      name: "Karthik S",
      role: "Software Developer @ Bosch",
      cohort: "Cohort 7",
      rating: 5,
      quote:
        "I joined the Grappler-Startup initiative and got my first internship building an MVP. That experience and portfolio got me hired at Bosch. GrapplTech literally launched my career.",
    },
  ];

  const curriculumFeatures = [
    {
      icon: FaLaptopCode,
      title: "Live System Design Workshops",
      desc: "Weekly live sessions designing real-world distributed systems with industry engineers.",
      tag: "Live",
      tagColor: "red",
    },
    {
      icon: FaComments,
      title: "1-on-1 Mock Interviews",
      desc: "Biweekly mock sessions with experienced engineers who give precise feedback on your actual performance gaps.",
      tag: "Mentored",
      tagColor: "blue",
    },
    {
      icon: FaChartLine,
      title: "AI-Powered Progress Tracking",
      desc: "Our platform tracks your weak zones across DSA, System Design, and Behavioral and adapts your roadmap daily.",
      tag: "AI-Driven",
      tagColor: "purple",
    },
    {
      icon: FaCertificate,
      title: "Industry-Recognized Certification",
      desc: "Our certification is recognized by 40+ hiring partners as a clear signal of interview-readiness.",
      tag: "Certified",
      tagColor: "green",
    },
    {
      icon: FaBriefcase,
      title: "Referral & Placement Support",
      desc: "Direct referral pipeline into our partner companies. Resume reviews, LinkedIn optimization, and warm intros.",
      tag: "Career",
      tagColor: "orange",
    },
    {
      icon: FaCalendarAlt,
      title: "Structured 12-Week Curriculum",
      desc: "From DSA fundamentals to senior-level architecture — a battle-tested curriculum built by engineers who've been there.",
      tag: "Structured",
      tagColor: "teal",
    },
  ];

  const faqs = [
    {
      q: "Who is GrapplTech for?",
      a: "GrapplTech is built for ambitious engineers — from final-year students to 3-5 year experienced professionals — who want to break into top-tier tech companies. Whether you're a fresher chasing your first offer or an experienced dev targeting FAANG, we have a track designed for you.",
    },
    {
      q: "How is this different from a bootcamp?",
      a: "Bootcamps teach you to code. We teach you to think like a senior engineer. Every module is mapped to actual gaps that cause candidates to fail top-tier interviews — not just syntax or theory.",
    },
    {
      q: "What's the time commitment per week?",
      a: "Our flagship 12-week cohort requires 15–20 hours per week. This includes live sessions, mock interviews, and self-paced assignments. We also offer a self-paced track for working professionals that can fit into 8–10 hours a week.",
    },
    {
      q: "Do you offer any placement guarantees?",
      a: "Yes. Cohort students who complete all assignments and mock interviews are covered by our placement guarantee — if you don't land a role within 6 months of graduating, you receive a full refund. No questions asked.",
    },
    {
      q: "What happens during the 12-week program?",
      a: "Weeks 1–4 cover DSA fundamentals, problem-solving patterns, and complexity analysis. Weeks 5–8 focus on system design — from basics to senior-level distributed systems. Weeks 9–12 are intensive interview simulation: mock loops, behavioral prep, offer negotiation, and referral activation.",
    },
    {
      q: "Who are the mentors and instructors?",
      a: "All mentors are active engineers at companies like Google, Meta, Amazon, Stripe, Zoho, and Cognizant with 4–12 years of industry experience. Every mentor is vetted for teaching ability, not just credentials.",
    },
    {
      q: "Is there support after I complete the program?",
      a: "Absolutely. GrapplTech alumni get lifetime access to our referral network, job board, and community Slack. We actively facilitate warm introductions to our hiring partners even after your cohort ends.",
    },
    {
      q: "Can I get a refund if the program isn't for me?",
      a: "We offer a 7-day full refund window after enrollment. If you attend the first two sessions and feel the program isn't the right fit, just let us know and we'll process a complete refund — no friction.",
    },
    {
      q: "What is the Grappler-Startup initiative?",
      a: "The Grappler-Startup initiative places our top students as intern engineers with early-stage startups building MVPs. Students get real production experience; startups get talented engineers at intern cost. It's a win-win that has helped 100+ students land their first internship.",
    },
    {
      q: "How do I enrol in the next cohort?",
      a: "Applications for the next cohort open every 6 weeks. Fill out the interest form on our homepage, clear a short screening call with our team, and you're in. Cohort seats are capped at 40 to ensure mentorship quality.",
    },
  ];

  return (
    <Box
      bg={mainBg}
      color={useColorModeValue("gray.800", "white")}
      overflowX="hidden"
    >
      <GlobalStyles />

      {/* ── 1. HERO ── */}
      <Container maxW="7xl" pt={{ base: 20, md: 28 }} pb={{ base: 14, md: 20 }}>
        <VStack spacing={6} textAlign="center">
          <Badge
            colorScheme="blue"
            variant="subtle"
            px={4}
            py={1}
            rounded="full"
          >
            The GrapplTech Way
          </Badge>
          <Heading
            fontSize={{ base: "3xl", sm: "4xl", md: "6xl", lg: "7xl" }}
            fontWeight="900"
            fontFamily="Bricolage Grotesque"
            lineHeight="1.1"
          >
            We don't just teach. <br />
            <chakra.span color={accentColor}>We Architect Careers.</chakra.span>
          </Heading>
          <Text
            maxW="2xl"
            fontSize={{ base: "lg", md: "xl" }}
            color={textMuted}
          >
            Bridging the gap between academic theory and industry reality
            through high-intensity mentorship and real-world project delivery.
          </Text>
        </VStack>
      </Container>

      {/* ── 2. TEAM ── */}
      <Container maxW="7xl" pb={{ base: 16, md: 24 }}>
        <TeamSlider />
      </Container>

      {/* ── 3. STATS — now a separate component ── */}
      <StatsSection />

      {/* ── 4. OUR STORY + BEANS + TIMELINE ── */}
      <Box position="relative" overflow="hidden">
        <BeansBackground color={accentColor} />

        <Container
          maxW="7xl"
          py={{ base: 16, md: 24 }}
          position="relative"
          zIndex={1}
        >
          {/* story header: text LEFT / image RIGHT — image raw, no box */}
          <Grid
            templateColumns={{ base: "1fr", lg: "1fr 1fr" }}
            gap={{ base: 10, lg: 16 }}
            mb={{ base: 14, md: 20 }}
            alignItems="center"
          >
            {/* left: text */}
            <VStack align="flex-start" spacing={6}>
              <HStack color={accentColor}>
                <Icon as={FaHistory} />
                <Text
                  fontWeight="bold"
                  letterSpacing="widest"
                  textTransform="uppercase"
                  fontSize="sm"
                >
                  Our Story
                </Text>
              </HStack>
              <Heading
                size={{ base: "xl", md: "2xl" }}
                fontFamily="Bricolage Grotesque"
                lineHeight="1.2"
              >
                Started from a "Why", <br />
                Now we are the "How".
              </Heading>
              <Text
                fontSize={{ base: "md", md: "lg" }}
                color={textMuted}
                lineHeight="tall"
              >
                Back in 2022, we noticed a painful pattern: brilliant graduates
                couldn't pass technical interviews because they lacked "industry
                context." We started GrapplTech to be that context.
              </Text>
              <Box
                p={{ base: 6, md: 8 }}
                bg={useColorModeValue("blue.50", "whiteAlpha.100")}
                rounded="2xl"
                position="relative"
                w="full"
              >
                <Icon
                  as={FaQuoteLeft}
                  position="absolute"
                  top={4}
                  left={4}
                  opacity={0.1}
                  boxSize={10}
                />
                <Text
                  fontStyle="italic"
                  fontSize={{ base: "md", md: "lg" }}
                  color={textMuted}
                >
                  "Our mission is to ensure that no ambitious learner is ever
                  held back by a lack of access to elite industry knowledge."
                </Text>
                <Text mt={4} fontWeight="bold">
                  — The Founders
                </Text>
              </Box>
            </VStack>

            {/* right: raw image — no box, no border, no shadow */}
            <Box
              w="full"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Image
                src={StoryImg}
                alt="GrapplTech story"
                w="full"
                maxH={{ base: "320px", md: "480px" }}
                objectFit="contain"
                objectPosition="center"
                draggable={false}
              />
            </Box>
          </Grid>

          {/* zig-zag timeline */}
          <ZigZagTimeline
            accentColor={accentColor}
            textMuted={textMuted}
            isLight={isLight}
          />
        </Container>
      </Box>

      {/* ── 5. CURRICULUM ── */}
      <Box
        py={{ base: 16, md: 24 }}
        bg={useColorModeValue("gray.50", "whiteAlpha.50")}
      >
        <Container maxW="7xl">
          <VStack
            mb={{ base: 10, md: 16 }}
            align="center"
            textAlign="center"
            spacing={4}
          >
            <Badge colorScheme="blue" px={3} py={1} rounded="md">
              The Program
            </Badge>
            <Heading
              size={{ base: "xl", md: "2xl" }}
              fontFamily="Bricolage Grotesque"
            >
              Everything you need to crack top tech
            </Heading>
            <Text
              color={textMuted}
              maxW="2xl"
              fontSize={{ base: "sm", md: "md" }}
            >
              Six pillars that turn raw talent into interview-ready engineers.
              No fluff, no filler — just the exact tools that move the needle.
            </Text>
          </VStack>
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={6}>
            {curriculumFeatures.map((f, i) => (
              <Box
                key={i}
                p={{ base: 6, md: 8 }}
                bg={cardBg}
                border="1px solid"
                borderColor={borderColor}
                rounded="2xl"
                transition="all 0.3s"
                _hover={{
                  transform: "translateY(-5px)",
                  shadow: "xl",
                  borderColor: accentColor,
                }}
              >
                <HStack justify="space-between" mb={5}>
                  <Icon
                    as={f.icon}
                    boxSize={{ base: 7, md: 9 }}
                    color={accentColor}
                  />
                  <Tag size="sm" colorScheme={f.tagColor} rounded="full">
                    <TagLabel>{f.tag}</TagLabel>
                  </Tag>
                </HStack>
                <Heading size="sm" mb={3}>
                  {f.title}
                </Heading>
                <Text
                  color={textMuted}
                  fontSize={{ base: "sm", md: "md" }}
                  lineHeight="tall"
                >
                  {f.desc}
                </Text>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ── 6. PLACEMENT WALL ── */}
      <Container maxW="7xl" py={{ base: 16, md: 24 }}>
        <VStack spacing={4} mb={14} textAlign="center">
          <Badge colorScheme="green" px={3} py={1} rounded="md">
            Placement Wall
          </Badge>
          <Heading
            size={{ base: "xl", md: "2xl" }}
            fontFamily="Bricolage Grotesque"
          >
            Our alumni work at the world's best
          </Heading>
          <Text color={textMuted} maxW="xl" fontSize={{ base: "sm", md: "md" }}>
            94% of our graduates land roles at their target companies within 6
            months of completing the program.
          </Text>
        </VStack>

        <CompaniesSlider accentColor={accentColor} speed={10} />

        <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={6} mt={14}>
          {[
            {
              label: "Avg. Package Increase",
              value: "2.8x",
              help: "vs. previous CTC",
              arrow: "increase",
            },
            {
              label: "Time to First Offer",
              value: "~11 wks",
              help: "after program completion",
              arrow: null,
            },
            {
              label: "Students with Multiple Offers",
              value: "61%",
              help: "had 2+ competing offers",
              arrow: "increase",
            },
          ].map((s, i) => (
            <Box
              key={i}
              p={{ base: 6, md: 8 }}
              bg={cardBg}
              border="1px solid"
              borderColor={borderColor}
              rounded="2xl"
              textAlign="center"
            >
              <Stat>
                <StatLabel
                  color={textMuted}
                  textTransform="uppercase"
                  letterSpacing="wider"
                  fontSize="xs"
                  fontWeight="bold"
                >
                  {s.label}
                </StatLabel>
                <StatNumber
                  fontSize={{ base: "3xl", md: "4xl" }}
                  fontWeight="900"
                  color={accentColor}
                  fontFamily="Bricolage Grotesque"
                >
                  {s.value}
                </StatNumber>
                <StatHelpText fontSize={{ base: "xs", md: "sm" }}>
                  {s.arrow && <StatArrow type={s.arrow} />}
                  {s.help}
                </StatHelpText>
              </Stat>
            </Box>
          ))}
        </SimpleGrid>
      </Container>

      {/* ── 7. VALUES ── */}
      <Box
        py={{ base: 16, md: 24 }}
        bg={useColorModeValue("gray.50", "whiteAlpha.50")}
      >
        <Container maxW="7xl">
          <VStack
            mb={{ base: 10, md: 16 }}
            align="center"
            textAlign="center"
            spacing={4}
          >
            <Badge colorScheme="cyan" px={3} py={1} rounded="md">
              Operating Principles
            </Badge>
            <Heading
              size={{ base: "xl", md: "2xl" }}
              fontFamily="Bricolage Grotesque"
            >
              How we build great things
            </Heading>
            <Text
              color={textMuted}
              maxW="2xl"
              fontSize={{ base: "sm", md: "md" }}
            >
              Our culture is the secret sauce to our students' success. We live
              by a set of principles that prioritize action, transparency, and
              deep engineering excellence.
            </Text>
          </VStack>
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={6}>
            {coreValues.map((v, i) => (
              <Box
                key={i}
                p={{ base: 6, md: 8 }}
                bg={cardBg}
                border="1px solid"
                borderColor={borderColor}
                rounded="2xl"
                transition="all 0.3s"
                _hover={{
                  transform: "translateY(-5px)",
                  shadow: "xl",
                  borderColor: accentColor,
                }}
              >
                <Icon
                  as={v.icon}
                  boxSize={{ base: 8, md: 10 }}
                  color={accentColor}
                  mb={5}
                />
                <Heading size="md" mb={4}>
                  {v.title}
                </Heading>
                <Text
                  color={textMuted}
                  fontSize={{ base: "sm", md: "md" }}
                  lineHeight="tall"
                >
                  {v.desc}
                </Text>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ── 8. TESTIMONIALS ── */}
      <Container maxW="7xl" py={{ base: 16, md: 24 }}>
        <VStack
          mb={{ base: 10, md: 16 }}
          align="center"
          textAlign="center"
          spacing={4}
        >
          <Badge colorScheme="yellow" px={3} py={1} rounded="md">
            Student Stories
          </Badge>
          <Heading
            size={{ base: "xl", md: "2xl" }}
            fontFamily="Bricolage Grotesque"
          >
            Straight from the engineers who made it
          </Heading>
          <Text
            color={textMuted}
            maxW="2xl"
            fontSize={{ base: "sm", md: "md" }}
          >
            Don't take our word for it. Here's what our alumni have to say about
            their GrapplTech journey.
          </Text>
        </VStack>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
          {testimonials.map((t, i) => (
            <Box
              key={i}
              p={{ base: 6, md: 8 }}
              bg={cardBg}
              border="1px solid"
              borderColor={borderColor}
              rounded="2xl"
              position="relative"
              transition="all 0.3s"
              _hover={{ transform: "translateY(-5px)", shadow: "xl" }}
            >
              <Icon
                as={FaQuoteLeft}
                position="absolute"
                top={6}
                right={6}
                opacity={0.08}
                boxSize={10}
              />
              <HStack mb={4}>
                {Array(t.rating)
                  .fill(0)
                  .map((_, j) => (
                    <Icon key={j} as={FaStar} color="yellow.400" boxSize={4} />
                  ))}
              </HStack>
              <Text
                fontSize={{ base: "sm", md: "md" }}
                lineHeight="tall"
                color={textMuted}
                mb={8}
                fontStyle="italic"
              >
                "{t.quote}"
              </Text>
              <Divider mb={6} />
              <Flex
                justify="space-between"
                align="center"
                flexWrap="wrap"
                gap={2}
              >
                <VStack align="flex-start" spacing={0}>
                  <Text fontWeight="bold" fontSize={{ base: "sm", md: "md" }}>
                    {t.name}
                  </Text>
                  <Text fontSize="sm" color={accentColor}>
                    {t.role}
                  </Text>
                </VStack>
                <Badge colorScheme="blue" variant="subtle" rounded="full">
                  {t.cohort}
                </Badge>
              </Flex>
            </Box>
          ))}
        </SimpleGrid>
      </Container>

      {/* ── 9. FAQ — ACCORDION ── */}
      <Box
        py={{ base: 16, md: 24 }}
        bg={useColorModeValue("gray.50", "whiteAlpha.50")}
      >
        <Container maxW="3xl" px={{ base: 4, md: 6 }}>
          {/* heading */}
          <VStack mb={{ base: 10, md: 14 }} textAlign="center" spacing={4}>
            <Badge colorScheme="purple" px={3} py={1} rounded="md">
              FAQs
            </Badge>
            <Heading
              fontSize={{ base: "3xl", md: "5xl", lg: "6xl" }}
              fontWeight="900"
              fontFamily="Bricolage Grotesque, sans-serif"
              lineHeight="1"
              letterSpacing="-2px"
              color={useColorModeValue("gray.900", "white")}
            >
              Got questions?
            </Heading>
            <Heading
              fontSize={{ base: "3xl", md: "5xl", lg: "6xl" }}
              fontWeight="900"
              fontFamily="Bricolage Grotesque, sans-serif"
              lineHeight="1"
              letterSpacing="-2px"
              color={accentColor}
              mt={-2}
            >
              We've got answers.
            </Heading>
            <Text
              color={textMuted}
              fontSize={{ base: "sm", md: "md" }}
              maxW="lg"
              pt={2}
            >
              Everything you need to know before you commit. If you still have
              questions, reach out — we're always happy to talk.
            </Text>
          </VStack>

          {/* accordion items */}
          <VStack spacing={3} align="stretch">
            {faqs.map((f, i) => (
              <FaqItem
                key={i}
                index={i}
                q={f.q}
                a={f.a}
                accentColor={accentColor}
                isOpen={openFaq === i}
                onToggle={() => setOpenFaq(openFaq === i ? null : i)}
              />
            ))}
          </VStack>
        </Container>
      </Box>

      {/* ── 10. FINAL CTA ── */}
      <Container maxW="7xl" py={{ base: 16, md: 24 }}>
        <MotionBox
          whileHover={{ scale: 1.005 }}
          bg={useColorModeValue("gray.900", "blue.600")}
          p={{ base: 10, md: 20 }}
          rounded="3xl"
          textAlign="center"
          color="white"
          position="relative"
          overflow="hidden"
          shadow="2xl"
        >
          <Box
            position="absolute"
            top="-50%"
            left="-10%"
            w="500px"
            h="500px"
            bg="whiteAlpha.200"
            rounded="full"
            filter="blur(80px)"
          />
          <VStack spacing={{ base: 7, md: 10 }} position="relative" zIndex={1}>
            <VStack spacing={4}>
              <Heading
                size={{ base: "xl", md: "2xl" }}
                fontFamily="Bricolage Grotesque"
              >
                Stop Learning in Isolation.
              </Heading>
              <Text
                fontSize={{ base: "lg", md: "xl" }}
                maxW="2xl"
                opacity={0.9}
              >
                Join a community of 6,000+ engineers building the future
                together.
              </Text>
            </VStack>
            <Button
              size="xl"
              h={{ base: "56px", md: "80px" }}
              px={{ base: 8, md: 16 }}
              fontSize={{ base: "md", md: "xl" }}
              bg="white"
              color="black"
              rounded="full"
              fontWeight="800"
              textTransform="uppercase"
              letterSpacing="wider"
              _hover={{ bg: "gray.100", transform: "translateY(-2px)" }}
              _active={{ transform: "scale(0.98)" }}
              rightIcon={<FaRocket />}
            >
              Join the Movement
            </Button>
          </VStack>
        </MotionBox>
      </Container>
    </Box>
  );
};

export default About;
